import { datadogRum } from '@datadog/browser-rum'
import PGServiceClient from '@/clients/PGServiceClient'
import WebsiteMgmtServiceClient from '@/clients/WebsiteMgmtServiceClient'
import { formatProviderName } from '@/utils/provider'
import { SOURCE, REVIEW_TYPE } from '@/constants'

function parseRecommend (value) {
  if (value === true || value === false) return value
  if (value == null || value === '') return null

  const normalized = String(value).trim().toLowerCase()
  if (['yes', 'true', '1'].includes(normalized)) return true
  if (['no', 'false', '0'].includes(normalized)) return false
  return null
}

function legacyId (value) {
  if (value == null || String(value).trim() === '') return null
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed <= 0) return null
  return parsed
}

function idOrEmpty (value) {
  return value == null || value === '' ? '' : String(value)
}

function requirePracticeUuid (state) {
  if (!state.practiceUuid) {
    throw new Error('practiceUuid URL parameter is required.')
  }
}

function getClient (state) {
  requirePracticeUuid(state)
  if (state.source === SOURCE.PO) {
    return new WebsiteMgmtServiceClient(state.practiceUuid)
  }
  if (state.source && state.source !== SOURCE.PG) {
    throw new Error(`Unknown reputation source: ${state.source}`)
  }
  return new PGServiceClient(state.practiceUuid)
}

const locationsRequests = new WeakMap()
const bootstrapRequests = new WeakMap()

function applyReviewContext (commit, state, context) {
  if (!context) return

  if (context.location_id != null) {
    const legacyLocationId = Number(context.location_id)
    commit('SET_ROUTING_LEGACY_LOCATION_ID', legacyLocationId)
    const selected = state.selectedLocation
    if (!selected || !selected.locationId) {
      commit('SET_SELECTED_LOCATION', { locationId: '', legacyLocationId, name: '' })
    }
  }

  if (context.doctor_id != null) {
    const legacyProviderId = Number(context.doctor_id)
    commit('SET_ROUTING_LEGACY_PROVIDER_ID', legacyProviderId)
    const selected = state.selectedProvider
    if (!selected || !selected.providerId) {
      commit('SET_SELECTED_PROVIDER', { providerId: '', legacyProviderId })
    }
  }

  const patient = {}
  if (context.first_name) patient.firstName = context.first_name
  // One character. The API withholds the full last name for HIPAA.
  if (context.last_name_initial) patient.lastName = context.last_name_initial
  if (Object.keys(patient).length) commit('SET_PATIENT_INFO', patient)

  if (context.calendar_booking_id != null) {
    commit('SET_CALENDAR_BOOKING_ID', Number(context.calendar_booking_id))
  }
}

async function runBootstrap ({ dispatch, state }) {
  if (state.source === SOURCE.PO) return null

  if (state.practiceUuid) {
    await dispatch('loadLocations')
  }

  const locations = state.locations || []
  const urlLocationId = state.routingLegacyLocationId
  const matched = urlLocationId != null
    ? locations.find(location => String(location.legacyLocationId) === String(urlLocationId))
    : null
  const legacyPracticeId = (matched && matched.legacyPracticeId) ||
    (locations[0] && locations[0].legacyPracticeId) ||
    null

  return dispatch('resolveReviewRouting', { legacyPracticeId })
}

const state = {
  source: null,
  practiceUuid: null,
  practiceName: null,
  review: null,
  campaignId: null,
  recipientId: null,
  ppLocationHash: null,
  providerHash: null,
  routingLegacyLocationId: null,
  routingLegacyProviderId: null,
  locations: [],
  locationsLoading: false,
  locationsError: null,
  selectedLocation: null,
  providers: [],
  providersLoading: false,
  providersError: null,
  selectedProvider: null,
  patientInfo: {
    firstName: '',
    lastName: '',
    email: ''
  },
  wouldRecommend: null,
  externalReviewUrl: null,
  calendarBookingId: null,
  routingResolved: false,
  routingRequested: false,
  campaignTracked: false,
  reviewType: null,
  rating: 0,
  content: '',
  isSubmitting: false,
  submitError: null,
  submitted: false
}

const getters = {
  source: state => state.source,
  practiceUuid: state => state.practiceUuid,
  practiceName: state => state.practiceName,
  locations: state => state.locations,
  locationsLoading: state => state.locationsLoading,
  locationsError: state => state.locationsError,
  selectedLocation: state => state.selectedLocation,
  providers: state => state.providers,
  providersLoading: state => state.providersLoading,
  providersError: state => state.providersError,
  selectedProvider: state => state.selectedProvider,
  patientInfo: state => state.patientInfo,
  wouldRecommend: state => state.wouldRecommend,
  externalReviewUrl: state => state.externalReviewUrl,
  ppLocationHash: state => state.ppLocationHash,
  providerHash: state => state.providerHash,
  routingResolved: state => state.routingResolved,
  reviewType: state => state.reviewType,
  rating: state => state.rating,
  content: state => state.content,
  isSubmitting: state => state.isSubmitting,
  submitError: state => state.submitError,
  submitted: state => state.submitted,
  headerTitle: state => {
    const name = formatProviderName(state.selectedProvider)
    return name || state.practiceName || ''
  },
  selectedProviderName: state => formatProviderName(state.selectedProvider),
  locationName: state => (state.selectedLocation && state.selectedLocation.name) || '',
  // PG matches the monolith: legacy integer ids. The practice id is on each
  // location, and the first loaded location still carries it before one is
  // selected. PO has no legacy ids, so it sends the practice, location, and
  // provider UUIDs.
  analyticsDims: state => {
    const location = state.selectedLocation
    const provider = state.selectedProvider
    if (state.source === SOURCE.PO) {
      return {
        practiceId: idOrEmpty(state.practiceUuid),
        locationId: idOrEmpty(location && location.locationId),
        providerId: idOrEmpty(provider && provider.providerId)
      }
    }
    const practiceId = (location && location.legacyPracticeId) ||
      (state.locations[0] && state.locations[0].legacyPracticeId) ||
      ''
    return {
      practiceId: idOrEmpty(practiceId),
      locationId: idOrEmpty(location && location.legacyLocationId),
      providerId: idOrEmpty(provider && provider.legacyProviderId)
    }
  }
}

const mutations = {
  SET_SOURCE (state, source) {
    state.source = source
  },
  SET_PRACTICE_UUID (state, uuid) {
    state.practiceUuid = uuid
  },
  SET_PRACTICE_NAME (state, name) {
    state.practiceName = name
  },
  SET_REVIEW (state, review) {
    state.review = review
  },
  SET_CAMPAIGN_ID (state, campaignId) {
    state.campaignId = campaignId
  },
  SET_RECIPIENT_ID (state, recipientId) {
    state.recipientId = recipientId
  },
  SET_PP_LOCATION_HASH (state, hash) {
    state.ppLocationHash = hash
  },
  SET_PROVIDER_HASH (state, hash) {
    state.providerHash = hash
  },
  SET_ROUTING_LEGACY_LOCATION_ID (state, locationId) {
    state.routingLegacyLocationId = locationId
  },
  SET_ROUTING_LEGACY_PROVIDER_ID (state, providerId) {
    state.routingLegacyProviderId = providerId
  },
  SET_LOCATIONS_LOADING (state, loading) {
    state.locationsLoading = loading
  },
  SET_LOCATIONS (state, locations) {
    state.locations = locations
  },
  SET_LOCATIONS_ERROR (state, error) {
    state.locationsError = error
  },
  SET_SELECTED_LOCATION (state, location) {
    state.selectedLocation = location
  },
  SET_PROVIDERS_LOADING (state, loading) {
    state.providersLoading = loading
  },
  SET_PROVIDERS (state, providers) {
    state.providers = providers
  },
  SET_PROVIDERS_ERROR (state, error) {
    state.providersError = error
  },
  SET_SELECTED_PROVIDER (state, provider) {
    state.selectedProvider = provider
  },
  SET_PATIENT_INFO (state, info) {
    state.patientInfo = { ...state.patientInfo, ...info }
  },
  SET_WOULD_RECOMMEND (state, value) {
    state.wouldRecommend = value
  },
  SET_EXTERNAL_REVIEW_URL (state, url) {
    state.externalReviewUrl = url
  },
  SET_CALENDAR_BOOKING_ID (state, calendarBookingId) {
    state.calendarBookingId = calendarBookingId
  },
  SET_ROUTING_RESOLVED (state, resolved) {
    state.routingResolved = resolved
  },
  SET_ROUTING_REQUESTED (state, requested) {
    state.routingRequested = requested
  },
  SET_CAMPAIGN_TRACKED (state, tracked) {
    state.campaignTracked = tracked
  },
  SET_REVIEW_TYPE (state, type) {
    state.reviewType = type
  },
  SET_RATING (state, rating) {
    state.rating = rating
  },
  SET_CONTENT (state, content) {
    state.content = content
  },
  SET_SUBMITTING (state, value) {
    state.isSubmitting = value
  },
  SET_SUBMIT_ERROR (state, error) {
    state.submitError = error
  },
  SET_SUBMITTED (state, value) {
    state.submitted = value
  }
}

const actions = {
  initFromParams ({ commit }, {
    source,
    practiceUuid,
    practiceName,
    locationId,
    providerId,
    ppLocation,
    providerHash,
    email,
    recommend,
    review,
    campaignId,
    recipientId
  }) {
    // An absent source means PG. Anything else is stored as-is so that an
    // unrecognized value surfaces through getClient instead of silently
    // routing to PG.
    commit('SET_SOURCE', source || SOURCE.PG)
    if (source === SOURCE.PO) {
      commit('SET_ROUTING_REQUESTED', true)
      commit('SET_ROUTING_RESOLVED', true)
    }
    if (practiceUuid) commit('SET_PRACTICE_UUID', practiceUuid)
    if (practiceName) commit('SET_PRACTICE_NAME', practiceName)
    if (review) commit('SET_REVIEW', review)
    if (campaignId) commit('SET_CAMPAIGN_ID', campaignId)
    if (recipientId) commit('SET_RECIPIENT_ID', recipientId)
    const parsedLocationId = legacyId(locationId)
    const parsedProviderId = legacyId(providerId)
    if (parsedLocationId != null) {
      commit('SET_ROUTING_LEGACY_LOCATION_ID', parsedLocationId)
      commit('SET_SELECTED_LOCATION', { locationId: '', legacyLocationId: parsedLocationId, name: '' })
    }
    if (parsedProviderId != null) {
      commit('SET_ROUTING_LEGACY_PROVIDER_ID', parsedProviderId)
      commit('SET_SELECTED_PROVIDER', { providerId: '', legacyProviderId: parsedProviderId })
    }
    if (ppLocation) commit('SET_PP_LOCATION_HASH', ppLocation)
    if (providerHash) commit('SET_PROVIDER_HASH', providerHash)
    if (email) commit('SET_PATIENT_INFO', { email })
    const parsedRecommend = parseRecommend(recommend)
    if (parsedRecommend !== null) commit('SET_WOULD_RECOMMEND', parsedRecommend)
  },

  loadLocations ({ commit, state }) {
    const existing = locationsRequests.get(state)
    if (existing) return existing

    commit('SET_LOCATIONS_LOADING', true)
    commit('SET_LOCATIONS_ERROR', null)

    // Register the promise before the fetch runs so a second caller, including
    // one scheduled before getClient throws, joins this request instead of
    // starting another that can overwrite locationsError or locationsLoading.
    const request = Promise.resolve().then(async () => {
      try {
        const { data } = await getClient(state).getLocations()
        commit('SET_LOCATIONS', data.locations || [])
      } catch (e) {
        commit('SET_LOCATIONS_ERROR', e)
      } finally {
        commit('SET_LOCATIONS_LOADING', false)
        if (locationsRequests.get(state) === request) locationsRequests.delete(state)
      }
    })

    locationsRequests.set(state, request)
    return request
  },

  async loadProviders ({ commit, state }, locationId) {
    commit('SET_PROVIDERS_LOADING', true)
    commit('SET_PROVIDERS_ERROR', null)
    try {
      const { legacyPracticeId, legacyLocationId } = state.selectedLocation || {}
      const { data } = await getClient(state).getProviders(locationId, legacyPracticeId, legacyLocationId)
      commit('SET_PROVIDERS', data.providers || [])
    } catch (e) {
      commit('SET_PROVIDERS_ERROR', e)
    } finally {
      commit('SET_PROVIDERS_LOADING', false)
    }
  },

  setLocationsError ({ commit }, error) {
    commit('SET_LOCATIONS_ERROR', error)
  },

  setProvidersError ({ commit }, error) {
    commit('SET_PROVIDERS_ERROR', error)
  },

  selectLocation ({ commit }, { location, preserveProvider = false } = {}) {
    commit('SET_SELECTED_LOCATION', location)
    commit('SET_PROVIDERS', [])
    if (!preserveProvider) commit('SET_SELECTED_PROVIDER', null)
  },

  selectProvider ({ commit }, provider) {
    commit('SET_SELECTED_PROVIDER', provider)
  },

  setPatientInfo ({ commit }, info) {
    commit('SET_PATIENT_INFO', info)
  },

  setWouldRecommend ({ commit }, value) {
    commit('SET_WOULD_RECOMMEND', value)
  },

  bootstrapReviewRouting (context) {
    if (context.state.routingRequested) return Promise.resolve(context.state.externalReviewUrl)
    const existing = bootstrapRequests.get(context.state)
    if (existing) return existing

    const request = runBootstrap(context)
    bootstrapRequests.set(context.state, request)
    return request
  },

  whenReviewContextReady ({ state }) {
    return bootstrapRequests.get(state) || Promise.resolve()
  },

  async resolveReviewRouting ({ commit, dispatch, state }, { legacyPracticeId } = {}) {
    if (state.routingRequested) return state.externalReviewUrl

    commit('SET_ROUTING_REQUESTED', true)

    if (state.source !== SOURCE.PG || legacyPracticeId == null) {
      commit('SET_EXTERNAL_REVIEW_URL', null)
      commit('SET_ROUTING_RESOLVED', true)
      await dispatch('trackCampaignResponse')
      return null
    }

    try {
      const context = await getClient(state).getReviewRouting({
        legacyPracticeId,
        legacyLocationId: state.routingLegacyLocationId,
        legacyProviderId: state.routingLegacyProviderId,
        review: state.review,
        ppLocation: state.ppLocationHash,
        providerHash: state.providerHash
      })
      commit('SET_EXTERNAL_REVIEW_URL', (context && context.external_review_url) || null)
      applyReviewContext(commit, state, context)
    } catch (error) {
      console.error('Failed to resolve review routing', error)
      commit('SET_EXTERNAL_REVIEW_URL', null)
    } finally {
      commit('SET_ROUTING_RESOLVED', true)
    }

    await dispatch('trackCampaignResponse')
    return state.externalReviewUrl
  },

  async trackCampaignResponse ({ commit, state }) {
    if (state.source !== SOURCE.PG ||
      state.campaignTracked ||
      !state.campaignId ||
      !state.recipientId ||
      !state.routingResolved ||
      state.wouldRecommend === null) return

    commit('SET_CAMPAIGN_TRACKED', true)
    try {
      await getClient(state).updateCampaignRecipient(
        state.campaignId,
        state.recipientId,
        {
          recommend: state.wouldRecommend ? 'YES' : 'NO',
          // The monolith records GOOGLE only for a Yes that has an external
          // destination. A No stays on PATIENTPOP even when that URL exists.
          routed_to: state.wouldRecommend && state.externalReviewUrl
            ? 'GOOGLE'
            : 'PATIENTPOP'
        }
      )
    } catch (error) {
      // Tracking is non-fatal and must never block the review flow.
      console.error('Failed to track campaign response', error)
    }
  },

  setReviewType ({ commit }, type) {
    commit('SET_REVIEW_TYPE', type)
  },

  setRating ({ commit }, rating) {
    commit('SET_RATING', rating)
  },

  setContent ({ commit }, content) {
    commit('SET_CONTENT', content)
  },

  async submitReview ({ commit, state }) {
    if (state.submitted) return
    commit('SET_SUBMITTING', true)
    commit('SET_SUBMIT_ERROR', null)
    try {
      const { patientInfo, rating, content, reviewType, practiceUuid, selectedLocation, selectedProvider } = state
      await getClient(state).submitReview({
        reviewerName: `${patientInfo.firstName} ${patientInfo.lastName}`.trim(),
        reviewerEmail: patientInfo.email,
        firstName: patientInfo.firstName,
        lastName: patientInfo.lastName,
        // PG private feedback collects no stars and is stored as 0. PO private
        // feedback collects a 1–5 rating on the form.
        rating: reviewType === REVIEW_TYPE.PRIVATE && state.source !== SOURCE.PO
          ? 0
          : rating,
        content,
        reviewType,
        practiceUuid,
        locationId: selectedLocation ? selectedLocation.locationId : null,
        providerId: selectedProvider ? selectedProvider.providerId : null,
        legacyPracticeId: selectedLocation ? selectedLocation.legacyPracticeId : null,
        legacyLocationId: selectedLocation ? selectedLocation.legacyLocationId : null,
        legacyProviderId: selectedProvider ? selectedProvider.legacyProviderId : null,
        calendarBookingId: state.calendarBookingId
      })
      commit('SET_SUBMITTED', true)
    } catch (e) {
      commit('SET_SUBMIT_ERROR', e)
      try {
        datadogRum.addError(e)
      } catch (_) {
        // RUM is only initialized outside local dev. A missing client must not hide the submit error.
      }
    } finally {
      commit('SET_SUBMITTING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
